// src/oura/docs/format.ts
//
// Words and small lookups for the documents screens: working days, "Read 4
// min ago", reply-by and late text, code chips, file types and trimming.
// Formatting only. Every state and every day count comes from the server;
// nothing here decides one. A null in gives a null (or "Not read yet") out,
// never a 0.

import { FileIcon, FileSpreadsheetIcon, FileTextIcon, type LucideIcon } from "lucide-react";
import {
    codeChipsOf,
    type BusinessDocuments,
    type DocsConnection,
    type CodeRow,
    type DocsFailure,
    type FileType,
    type MerchantDocument,
    type StaffDocument,
} from "@service/aeo/clientDocs";
import { isShortOutlet, parseTitleCodes } from "@service/aeo/clientDocsRules";
import type { ChangeRequest } from "@service/aeo/clientReview";
import { dayDate, monthLabel } from "../lib/oura";
import { DOC_STATUS, type DocStatusKey, type DocStatusStyle, type Family, type RequestStatusKey, type Tone } from "../ui/tokens";

/* ------------------------------------------------------------------------- *
 * Counting words
 * ------------------------------------------------------------------------- */

/** "1 doc", "3 docs". */
export function plural(n: number, noun: string, nouns = `${noun}s`): string {
    return `${n} ${n === 1 ? noun : nouns}`;
}

/** "1 needs us", "3 need us", "0 need us". */
export function needUsText(n: number): string {
    return `${n} ${n === 1 ? "needs" : "need"} us`;
}

/**
 * A row's wait, short: "Waiting 6 working days"; "Waiting under 1 working
 * day" for 0, which on a weekend or a holiday is not "today". Its group head
 * says on whom. Null for an unknown count.
 */
export function waitingDaysText(n: number | null | undefined): string | null {
    if (n === null || n === undefined || !Number.isFinite(n)) return null;
    return n <= 0 ? "Waiting under 1 working day" : `Waiting ${plural(n, "working day")}`;
}

/**
 * Who a doc waits on and for how long, in the status tag's words, the same
 * on the board and on All clients: "Waiting on us for 6 working days",
 * "Waiting on client for 3 working days". 0 working days reads "for under 1
 * working day": on a weekend or a holiday that is not "today". Null when
 * nobody waits or the count is unknown.
 */
export function waitingOnText(who: "us" | "client" | null | undefined, days: number | null | undefined): string | null {
    if (!who || days === null || days === undefined || !Number.isFinite(days)) return null;
    return days <= 0 ? `Waiting on ${who} for under 1 working day` : `Waiting on ${who} for ${plural(days, "working day")}`;
}

/**
 * "3 days late"; null when not late or unknown. Draw it in LATE_TEXT. Counted
 * in working days, but said as plain days, the way What to do next and Delivery
 * say it: one wording for late on every screen (Jack, 22 Oct 2026).
 */
export function lateText(days: number | null | undefined): string | null {
    if (!days || days <= 0) return null;
    return `${plural(days, "day")} late`;
}

/**
 * An ask's late note: "7 days late"; null when not late or unknown. The server
 * counts an ask's lateness (`lateDays`) in calendar days and a doc's in working
 * days; both now read "N days late", as Overview always said them, so late is
 * one wording everywhere (Jack, 22 Oct 2026). Draw it in LATE_TEXT.
 */
export function calendarLateText(days: number | null | undefined): string | null {
    if (!days || days <= 0) return null;
    return `${plural(days, "day")} late`;
}

/**
 * A late doc's late note: "3 working days late", or "Was due Fri 2 Oct" when
 * the count is 0 or unknown. A doc past its reply-by on the weekend or holiday
 * straight after it is late with 0 working days, and must still say so.
 * Null when the doc is not late.
 */
export function docLateText(d: { late: boolean | null; lateWorkingDays: number | null; replyBy: string | null }): string | null {
    if (!d.late) return null;
    return lateText(d.lateWorkingDays) ?? merchantReplyByText({ replyBy: d.replyBy, late: true }) ?? "Late";
}

/**
 * A late doc's note, saying whose lateness it is. On a doc that waits on us
 * the lateness is the client's (the questions still with them are past
 * reply-by), so the note says so: "Client 3 working days late", "Client reply
 * was due Fri 2 Oct", never a bare "3 working days late" beside "Waiting on
 * us". Otherwise docLateText. Null when the doc is not late. The board and
 * All clients share it, so the two never word it differently.
 */
export function docLateNote(
    d: { late: boolean | null; lateWorkingDays: number | null; replyBy: string | null },
    who: "us" | "client" | null | undefined,
): string | null {
    if (who !== "us") return docLateText(d);
    if (!d.late) return null;
    const days = lateText(d.lateWorkingDays);
    if (days) return `Client ${days}`;
    const due = docDate(d.replyBy);
    return due ? `Client reply was due ${due}` : "Client late";
}

/** "Replies in 2 working days", "Never replied", or null when nothing has been sent. */
export function typicalReplyText(days: number | null, basis: { answered: number; sent: number }): string | null {
    if (days !== null) return `Replies in ${plural(days, "working day")}`;
    if (basis.sent > 0) return "Never replied";
    return null;
}

/** "7 out · 3 back"; null when the comments were not read (a PDF, a Sheet, or no read yet). */
export function questionsText(q: StaffDocument["questions"]): string | null {
    if (!q) return null;
    return `${q.out} out · ${q.back} back`;
}

/** Merchant: "2 questions open", "Nothing to answer", "All answered"; null when not read. */
export function merchantQuestionsText(q: MerchantDocument["questions"]): string | null {
    if (!q) return null;
    if (q.waiting > 0) return `${plural(q.waiting, "question")} open`;
    if (q.out === 0) return "Nothing to answer";
    return "All answered";
}

/* ------------------------------------------------------------------------- *
 * Times and dates (Malaysian time)
 * ------------------------------------------------------------------------- */

const MY_OFFSET_MS = 8 * 3_600_000;
const myYmd = (t: number) => new Date(t + MY_OFFSET_MS).toISOString().slice(0, 10);

/** "Tue 22 Sep" for a YYYY-MM-DD or an instant, read in Malaysian time; null when missing. */
export function docDate(value: string | null | undefined): string | null {
    if (!value) return null;
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return dayDate(value);
    const t = Date.parse(value);
    return Number.isNaN(t) ? null : dayDate(myYmd(t));
}

/** "Thu 15 Oct 2026". docDate carries the year itself now (23 Oct 2026); kept so its callers read the same. */
export function docDateYear(value: string | null | undefined): string | null {
    return docDate(value);
}

/** "just now", "4 min ago", "3 hours ago", "yesterday", "on Tue 22 Sep"; null when missing. */
export function agoText(iso: string | null | undefined, now: number = Date.now()): string | null {
    if (!iso) return null;
    const t = Date.parse(iso);
    if (Number.isNaN(t)) return null;
    const minutes = Math.floor((now - t) / 60_000);
    if (minutes < 1) return "just now";
    if (minutes < 60) return `${minutes} min ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${plural(hours, "hour")} ago`;
    const d = new Date(myYmd(now));
    d.setUTCDate(d.getUTCDate() - 1);
    if (myYmd(t) === d.toISOString().slice(0, 10)) return "yesterday";
    const day = docDate(iso);
    return day ? `on ${day}` : null;
}

/**
 * agoText without its leading "on", for after a word that is already the
 * preposition: "from Tue 22 Sep", "Asked Tue 22 Sep", never "from on".
 */
export function fromAgoText(iso: string | null | undefined, now: number = Date.now()): string | null {
    return agoText(iso, now)?.replace(/^on /, "") ?? null;
}

/**
 * A row's time standing on its own, in sentence case: "3 hours ago",
 * "Yesterday", "Just now", "Sat 26 Sep". Null when missing. After a word,
 * use fromAgoText: "Replied yesterday".
 */
export function whenText(iso: string | null | undefined, now: number = Date.now()): string | null {
    const t = fromAgoText(iso, now);
    return t ? `${t.charAt(0).toUpperCase()}${t.slice(1)}` : null;
}

/** "Read 4 min ago", "Read yesterday"; "Not read yet" for a null. */
export function readAgoText(iso: string | null | undefined, now: number = Date.now()): string {
    const ago = agoText(iso, now);
    return ago ? `Read ${ago}` : "Not read yet";
}

/**
 * True when a read time is older than 3 sync runs (15 minutes at the 5-minute
 * sync): the reads have stopped, so the screen must not present it as current.
 */
export function isReadStale(iso: string | null | undefined, everyMinutes = 5, now: number = Date.now()): boolean {
    if (!iso) return false;
    const t = Date.parse(iso);
    return !Number.isNaN(t) && now - t > everyMinutes * 3 * 60_000;
}

/** "Reply by Fri 25 Sep"; null with no reply-by date. */
export function replyByText(replyBy: string | null | undefined): string | null {
    const d = docDate(replyBy);
    return d ? `Reply by ${d}` : null;
}

/** Merchant words: "Reply by Fri 25 Sep", or "Was due Fri 25 Sep" once it has passed. */
export function merchantReplyByText(doc: Pick<MerchantDocument, "replyBy" | "late">): string | null {
    const d = docDate(doc.replyBy);
    if (!d) return null;
    return doc.late ? `Was due ${d}` : `Reply by ${d}`;
}

/** "Shared Tue 22 Sep"; null when not shared or unknown. */
export function sharedText(sharedAt: string | null | undefined): string | null {
    const d = docDate(sharedAt);
    return d ? `Shared ${d}` : null;
}

/**
 * The plan window a code's doc is due in, named: "the end of the Foundation
 * build" for month 1, "the end of plan month 3" after it. Null when unknown.
 */
export function planWindowText(month: number | null | undefined): string | null {
    if (!month || !Number.isFinite(month) || month < 1) return null;
    return month === 1 ? "the end of the Foundation build" : `the end of plan month ${month}`;
}

/**
 * Why a code's due date is a month's last working day, for a `title` beside
 * it: the timeline dates a deliverable to a month, never a week.
 */
export const DUE_BY_NOTE = "Oura's timeline dates each deliverable to a plan month, never a week, so its doc is due by that month's last working day. Month 1 is the Foundation build, 20 working days from Day 1.";

/**
 * When a code's doc is due: "Due by Fri 16 Oct", or "Was due Mon 14 Sep"
 * once a code not sent yet is past it. `window` adds the plan window,
 * "Due by Fri 16 Oct, the end of plan month 2". Null with no date.
 */
export function codeDueText(row: Pick<CodeRow, "expectedBy" | "state" | "late"> & { expectedMonth?: number | null }, window = false): string | null {
    const d = docDate(row.expectedBy);
    if (!d) return null;
    const text = isCodeOverdue(row) ? `Was due ${d}` : `Due by ${d}`;
    const named = window ? planWindowText(row.expectedMonth) : null;
    return named ? `${text}, ${named}` : text;
}

/** A code whose doc is not sent yet and past its due date: draw its date in LATE_TEXT. */
export function isCodeOverdue(row: Pick<CodeRow, "state" | "late">): boolean {
    return row.state === "not_sent_yet" && row.late === true;
}

/** codeDueText, kept under its first name. */
export function expectedByText(row: Pick<CodeRow, "expectedBy" | "state" | "late"> & { expectedMonth?: number | null }): string | null {
    return codeDueText(row);
}

/** "October 2026" for a small change's "2026-10". */
export function smallChangeMonthText(month: string | null | undefined): string | null {
    return month && /^\d{4}-\d{2}$/.test(month) ? monthLabel(month) : null;
}

/* ------------------------------------------------------------------------- *
 * Oura's own Google account, as the server names it
 * ------------------------------------------------------------------------- */

/**
 * Oura's Google account is NOT the same address everywhere: the server's
 * `OURA_DRIVE_SUBJECT` is oura@ourteam.ai on production and
 * no-reply@ourteam.ai on staging. Route 1's `actingAs` is the only truth, so
 * nothing staff-facing writes the address as a literal — a staffer who shared
 * a client's folder with the wrong account got a folder that would not read
 * (#3974).
 *
 * Absent is unknown, not "none": while the connection is still being read the
 * account is simply not named. A server that says there is none gets
 * Connectors' own sentence, so the two screens agree.
 */
export function ouraAccountOf(conn: Pick<DocsConnection, "status" | "actingAs"> | null | undefined): string | null {
    const account = conn?.actingAs?.trim() || null;
    return conn && conn.status !== "not_connected" ? account : null;
}

/** Whether the server has answered that there is no account at all (not merely that we have not read it yet). */
export function ouraAccountMissing(conn: Pick<DocsConnection, "status" | "actingAs"> | null | undefined): boolean {
    return Boolean(conn) && ouraAccountOf(conn) === null;
}

/** Connectors' sentence for an account that is not set up, so every screen says it the same way. */
export const OURA_ACCOUNT_NOT_SET_UP = "Oura's Google account isn't set up yet.";

/**
 * The folder card's hint: "Give no-reply@ourteam.ai edit access to the folder
 * first." Unknown names no address; none at all says so.
 */
export function folderAccessHint(conn: Pick<DocsConnection, "status" | "actingAs"> | null | undefined): string {
    if (ouraAccountMissing(conn)) return OURA_ACCOUNT_NOT_SET_UP;
    const account = ouraAccountOf(conn);
    return `Give ${account ?? "Oura's Google account"} edit access to the folder first.`;
}

/**
 * Oura's own Google account as THIS doc's share list names it: the owner row
 * flagged `ours`. The doc already carries it, so a drawer or a comment box can
 * name the account without a second read, and still never writes it as a
 * literal. Null when the list says nothing.
 */
export function ouraAccountOnDoc(doc: Pick<StaffDocument, "sharing" | "owner">): string | null {
    // THE FILE'S OWNER, when the owner is ours -- not the first ourteam
    // address on it. A client folder is shared with the team, so `sharedWith`
    // carries several of ours, and taking the first gave whichever sorted
    // earliest: a colleague with view access, named on screen as the account
    // Oura reads with.
    //
    // Null rather than a guess when the owner is outside ourteam (a linked
    // doc someone else owns). The caller prefers the connection's `actingAs`
    // anyway; this is only its fallback.
    const owner = doc.owner?.trim();
    const ours = doc.sharing.sharedWith.find((p) => p.email?.trim().toLowerCase() === owner?.toLowerCase());
    return owner && ours?.ours ? owner : null;
}

/** "It checks every 5 minutes, as no-reply@ourteam.ai." The account is left unsaid until it is known. */
export function folderReadsAsText(conn: Pick<DocsConnection, "status" | "actingAs"> | null | undefined): string {
    const account = ouraAccountOf(conn);
    return account ? `It checks every 5 minutes, as ${account}.` : "It checks every 5 minutes.";
}

/* ------------------------------------------------------------------------- *
 * Codes
 * ------------------------------------------------------------------------- */

/** The chip family for a code: S, A, B, G, or X for custom work. */
export function codeFamily(code: string): Family {
    const f = code.trim().charAt(0).toLowerCase();
    return f === "s" || f === "a" || f === "b" || f === "g" ? f : "x";
}

/**
 * "S2-MK", or "S2" with no outlet. A PLACE-NAME outlet stays off the code:
 * "S2-Mont Kiara" would not read back as a code, and the backend writes plain
 * codes for one (`title_of`, #5254). The place is named beside the doc type
 * instead, "GBP Pack, Mont Kiara".
 */
export function codeLabel(code: string, outlet: string | null | undefined): string {
    return isShortOutlet(outlet) ? `${code}-${outlet}` : code;
}

export interface CodeChip {
    code: string;
    outlet: string | null;
    /** "S2-MK" */
    label: string;
    family: Family;
}

/** A doc's code chips, with its outlet on the code it belongs to. [] for a doc with no code. */
export function docCodeChips(doc: { codes: readonly string[]; outlet: string | null }): CodeChip[] {
    return codeChipsOf(doc.codes, doc.outlet).map((c) => ({ ...c, family: codeFamily(c.code) }));
}

/** ", every outlet" at the end of a plan line: the whole contract's word, not one outlet's doc. */
const EVERY_OUTLET = /,\s*(?:every|each|all)\s+outlets?$/i;

/**
 * A plan line for a code that carries an outlet: "Google Business Profile,
 * every outlet" for S2-MK reads "Google Business Profile". The line as it is
 * with no outlet. The merchant's documents name a doc's purpose this way.
 */
export function outletPlanLine(label: string, outlet: string | null | undefined): string {
    if (!outlet) return label;
    return label.replace(EVERY_OUTLET, "").trim() || label;
}

/** Staff words for a code's plan line, naming its outlet: "Google Business Profile, MK outlet". The line as it is with no outlet. */
export function codeRowName(label: string, outlet: string | null | undefined): string {
    return outlet ? `${outletPlanLine(label, outlet)}, ${outlet} outlet` : label;
}

export interface PickableCode {
    code: string;
    outlet: string | null;
    /** "S2-MK" */
    label: string;
    /** The contract's line, "Google Business Profile, every outlet". */
    name: string | null;
    family: Family;
}

/** What "Pick a code" offers: the contract's codes, each outlet already in use as its own choice. */
export function pickableCodes(board: Pick<BusinessDocuments, "codes">): PickableCode[] {
    return board.codes
        .filter((r) => r.inContract)
        .map((r) => ({ code: r.code, outlet: r.outlet, label: codeLabel(r.code, r.outlet), name: r.label, family: codeFamily(r.code) }));
}

/* ------------------------------------------------------------------------- *
 * Files and titles
 * ------------------------------------------------------------------------- */

export interface FileTypeStyle {
    /** "Google Doc", "Word file", "PDF", "Sheet", "File". */
    label: string;
    icon: LucideIcon;
    /** IconSquare tone: Google Doc and Word blue, Sheet green, PDF red, other gray. */
    tone: Tone;
    /** The staff link: Word files open in Google Docs too. */
    openLabel: string;
    /** Whether Oura reads comments in this kind of file. */
    readsComments: boolean;
}

export const FILE_TYPE: Record<FileType, FileTypeStyle> = {
    gdoc: { label: "Google Doc", icon: FileTextIcon, tone: "blue", openLabel: "Open in Google Docs", readsComments: true },
    word: { label: "Word file", icon: FileTextIcon, tone: "blue", openLabel: "Open in Google Docs", readsComments: true },
    pdf: { label: "PDF", icon: FileIcon, tone: "red", openLabel: "Open in Google Drive", readsComments: false },
    sheet: { label: "Sheet", icon: FileSpreadsheetIcon, tone: "green", openLabel: "Open in Google Sheets", readsComments: false },
    other: { label: "File", icon: FileIcon, tone: "gray", openLabel: "Open in Google Drive", readsComments: false },
};

/** A file type's style, with the plain file style for a type the server sends that this build does not know. */
export function fileStyle(t: string): FileTypeStyle {
    return FILE_TYPE[t as FileType] ?? FILE_TYPE.other;
}

/** The merchant's link, the scope's words. */
export const MERCHANT_OPEN_LABEL = "Open the document";

/**
 * "Answer Plan (S1, S3, B1, G4)" to "Answer Plan": the codes already show as
 * chips. Codes split by commas, spaces or both all go, so older names too.
 */
export function displayTitle(title: string): string {
    const stripped = title.split("\n")[0].replace(/\s*\((?:[A-Z]\d+(?:-[A-Z]{2,4})?[\s,]*)+\)\s*$/, "").trim();
    return stripped || title.trim();
}

/** The doc type from the name, else the page title, else the file name. */
export function docTypeTitle(doc: Pick<StaffDocument, "docType" | "pageTitle" | "name">): string {
    if (doc.docType) return doc.docType;
    if (doc.pageTitle) return displayTitle(doc.pageTitle);
    return doc.name;
}

/**
 * A staff row's title: the doc type from the name, else the page title, else
 * the file name. A pick keeps the title staff saw before it. A file with no
 * code anywhere was known by its file name, and that name is all that tells
 * it apart from the doc whose name carries the codes (a Word copy of the
 * Answer Plan beside the Answer Plan). A doc whose page title carries codes
 * was known by its type, and a corrected code keeps it.
 */
export function staffDocTitle(doc: Pick<StaffDocument, "codeSource" | "docType" | "pageTitle" | "name" | "outlet">): string {
    if (doc.codeSource === "staff" && !parseTitleCodes(doc.pageTitle)) return doc.name;
    return withOutletPlace(docTypeTitle(doc), outletPlaceOf(doc));
}

/**
 * A display title with the outlet a doc is for (#4702): two outlets' GBP Packs read "GBP Pack,
 * Mont Kiara" and "GBP Pack, Bukit Bintang", not "GBP Pack" twice. Never doubled: a title that
 * already ends with the place keeps it once, and a short outlet code ("MK") is never added to a
 * type that already names its place in its own words ("GBP Pack Mont Kiara", an older name).
 * A display title only: the file name's doc type stays `docTypeTitle`.
 */
export function withOutletPlace(type: string, place: string | null): string {
    if (!place) return type;
    const t = squash(type);
    const p = squash(place);
    if (t === p || t.endsWith(`, ${p}`) || t.endsWith(` ${p}`)) return type;
    // A known type on its own ("GBP Changes Applied") names no place, though it starts with another one (Jack, #4704).
    if (isShortOutlet(place.trim()) && !PLACE_BEARING_TYPE.includes(t) && PLACE_BEARING_TYPE.some((base) => t.startsWith(`${base} `))) return type;
    return `${type}, ${place.trim()}`;
}

/** The per-outlet doc types, by any name they were filed under; a type longer than one of these names its place. */
const PLACE_BEARING_TYPE = ["gbp pack", "gbp changes", "gbp changes applied", "google profile pack"];

function squash(text: string): string {
    return text.trim().replace(/\s+/g, " ").toLowerCase();
}

/**
 * The outlet a doc is for, by its place name: line 1's own after its type ("GBP Pack, Mont Kiara
 * (S2-MK)" or the older "GBP Pack Mont Kiara" give "Mont Kiara"), else the doc's outlet ("Mont
 * Kiara", or "MK" when nothing names the place). Null for a doc with no outlet.
 */
export function outletPlaceOf(doc: Pick<StaffDocument, "docType" | "pageTitle" | "outlet">): string | null {
    if (!doc.outlet) return null;
    const line = doc.pageTitle ? displayTitle(doc.pageTitle) : "";
    const type = doc.docType?.trim() ?? "";
    const after = line.slice(type.length);
    // the type as a whole word: "GBP Packages" is not "GBP Pack" with the place "ages" (Jack, #4704)
    if (line && type && line.slice(0, type.length).toLowerCase() === type.toLowerCase() && (after.startsWith(",") || /^\s/.test(after))) {
        const rest = after.trim();
        const place = rest.startsWith(",") ? rest.slice(1).trim() : rest;
        if (place) return place;
    }
    return doc.outlet.trim() || null;
}

/** Shortens long text at a word, with an ellipsis. The full text belongs in a `title` tooltip. */
export function trimText(text: string, max: number): string {
    const t = text.replace(/\s+/g, " ").trim();
    if (t.length <= max) return t;
    const cut = t.slice(0, max - 1);
    const space = cut.lastIndexOf(" ");
    return `${(space > max * 0.6 ? cut.slice(0, space) : cut).replace(/[\s,.;:]+$/, "")}…`;
}

/* ------------------------------------------------------------------------- *
 * States
 * ------------------------------------------------------------------------- */

/** Which status a staff doc shows: "No code yet" for a file with none, null when its comments are not read. */
export function docStatusKey(doc: Pick<StaffDocument, "codes" | "notClientDoc" | "state">): DocStatusKey | null {
    if (!doc.notClientDoc && doc.codes.length === 0) return "no_code";
    return doc.state;
}

/** The staff and merchant words and tones for a doc; null when there is no state to show. */
export function docStatus(doc: Pick<StaffDocument, "codes" | "notClientDoc" | "state">): DocStatusStyle | null {
    const key = docStatusKey(doc);
    // A state this build does not know shows no status, never a crash.
    return key ? DOC_STATUS[key] ?? null : null;
}

/** A change request's tag key: Done, then This month's small change, then Answered, else Needs us. */
export function requestStatusKey(req: Pick<ChangeRequest, "status" | "smallChange">): RequestStatusKey {
    if (req.status === "done") return "done";
    if (req.smallChange) return "small_change";
    if (req.status === "answered") return "answered";
    return "open";
}

/**
 * The small changes filed beyond the first in their Malaysian calendar month
 * (staff filed one "anyway" over the limit), by id. The first filed in a
 * month is the plan's; any later one that month is extra. Ordered by when
 * each was filed, else when it was asked. Done ones still count: the month
 * used its small change.
 */
export function extraSmallChangeIds(
    rows: ReadonlyArray<{ id: string; at: string; smallChange: { month: string; filedAt?: string | null } | null }>,
): Set<string> {
    const byMonth = new Map<string, Array<{ id: string; t: string }>>();
    for (const r of rows) {
        if (!r.smallChange) continue;
        const list = byMonth.get(r.smallChange.month) ?? [];
        list.push({ id: r.id, t: r.smallChange.filedAt || r.at || "" });
        byMonth.set(r.smallChange.month, list);
    }
    const extra = new Set<string>();
    byMonth.forEach((list) => {
        list.sort((a, b) => a.t.localeCompare(b.t) || a.id.localeCompare(b.id));
        list.slice(1).forEach((x) => extra.add(x.id));
    });
    return extra;
}

/** Oldest wait first; a doc with no wait goes last. */
export function byOldestWait<T extends { waitingSince: string | null }>(a: T, b: T): number {
    return (a.waitingSince ?? "9999").localeCompare(b.waitingSince ?? "9999");
}

/** The docs filed under one code row, in the board's order. */
export function docsForRow(board: Pick<BusinessDocuments, "documents">, row: Pick<CodeRow, "documentIds">): StaffDocument[] {
    const byId = new Map(board.documents.map((d) => [d.id, d] as const));
    return row.documentIds.map((id) => byId.get(id)).filter((d): d is StaffDocument => !!d);
}

/* ------------------------------------------------------------------------- *
 * Failures
 * ------------------------------------------------------------------------- */

/**
 * One plain sentence for a failed read. `what` names the thing ("the
 * documents", "the folder"). A server refusal keeps the server's sentence.
 */
export function docsFailureText(f: DocsFailure, what = "the documents"): string {
    switch (f.reason) {
        case "notServed":
            return `Oura can't read ${what} here yet.`;
        case "forbidden":
            return `This account can't see ${what}.`;
        case "unauthorised":
            return "Your session has ended. Sign in again.";
        case "notFound":
            return f.message && f.message !== "Not found." ? f.message : `Oura can't find ${what}.`;
        case "rejected":
            return f.message;
        default:
            return `Could not read ${what}. It will retry in a minute.`;
    }
}
